import { RootState } from 'redux/schemas';
import { createSelector } from 'reselect';
import { values } from 'lodash';
import { AnalyticsPageType, AverageChangeRow, ConfigurationType, CsatSummary, NpsCsatSummary, NpsSummary, QuantileDistribution, Thresholds } from 'redux/schemas/app/analytics';
import { find } from 'underscore';
import { extractTextFromHTML } from 'shared/utils';
import { getCurrentCourse } from './course';
import { Distribution } from 'redux/schemas/api/analytics';

const userEnrollments = (state: RootState) => state.models.enrollments;
const allCourses = (state: RootState) => state.models.courses;
const accessibleCourses = (state: RootState) => state.app.analytics.accessibleCourses || [];

const getUserEnrollments = createSelector(
  [userEnrollments, allCourses],
  (enrollments, courses) => values(enrollments)
    .map((enrollment) => {
      const course = courses[enrollment.courseCatalogId];
      return {
        ...enrollment,
        name: course?.name,
        catalogId: enrollment.courseCatalogId,
        isPrimary: course?.isPrimary,
        isCohort: course?.primaryExists,
      };
    })
    .filter(enrollment => enrollment.isInstructorOrReportAdmin)
    .sort((a, b) => {
      const aDate = a.lastActivity || a.enrolledAt;
      const bDate = b.lastActivity || b.enrolledAt;
      return new Date(bDate).getTime() - new Date(aDate).getTime();
    }),
);

const getAccessiblePrimaryCourses = createSelector(
  [accessibleCourses],
  (courses) => courses.map((course) => ({
    ...course,
    isPrimary: true,
  })),
);

export const getCourseOptions = createSelector(
  [
    getUserEnrollments,
    getAccessiblePrimaryCourses,
    (_state: RootState, analyticsPageType: AnalyticsPageType) => analyticsPageType,
  ],
  (enrollments, primaryCourses, analyticsPageType) => (analyticsPageType === AnalyticsPageType.COURSE_ANALYTICS ? enrollments : primaryCourses),
);

export const getQuestionSets = createSelector(
  [
    (state: RootState) => state.app.analytics.questionSets,
    (state: RootState) => state.app.currentCatalogId,
    (_state: RootState, analyticsPageType: AnalyticsPageType) => analyticsPageType,
    (_state: RootState, _analyticsPageType: AnalyticsPageType, catalogId?: string) => catalogId,
  ],
  (questionSets, currentCatalogId, analyticsPageType, catalogId) => questionSets?.[catalogId ?? currentCatalogId]?.[analyticsPageType] || {},
);

export const getSurveyQuestionSets = createSelector(
  [
    (state: RootState) => state.app.analytics.surveyQuestionsets,
    (state: RootState) => state.app.currentCatalogId,
    (_state: RootState, analyticsPageType: AnalyticsPageType) => analyticsPageType,
    (_state: RootState, _analyticsPageType: AnalyticsPageType, catalogId?: string) => catalogId,
  ],
  (
    questionSets,
    currentCatalogId,
    analyticsPageType,
    catalogId,
  ) => questionSets?.[catalogId ?? currentCatalogId]?.[analyticsPageType]?.surveys || [],
);

export const showPrePostTab = createSelector(
  [
    getCurrentCourse,
    (state: RootState) => state.app.analytics.surveyQuestionsets,
    (_state: RootState, analyticsPageType: AnalyticsPageType) => analyticsPageType,
  ],
  (currentCourse, questionSets, analyticsPageType) => {
    const catalogId = currentCourse?.catalogId;

    const questionSetData = questionSets?.[catalogId]?.[analyticsPageType] || {};
    const surveyQuestionSets = questionSetData.surveys || [];
    return surveyQuestionSets.length > 0;
  },
);

export const getSelectedCourse = createSelector(
  [
    allCourses,
    getAccessiblePrimaryCourses,
    (_state: RootState, analyticsPageType: AnalyticsPageType) => analyticsPageType,
    (_state: RootState, _analyticsPageType: AnalyticsPageType, selectedCatalogId?: string) => selectedCatalogId,
  ],
  (courses, primaryCourses, analyticsPageType, selectedCatalogId) => {
    if (analyticsPageType === AnalyticsPageType.COURSE_ANALYTICS) {
      return courses[selectedCatalogId];
    }
    return primaryCourses.find(course => course.catalogId === selectedCatalogId);
  },
);

export const getExistingConfiguration = createSelector(
  [
    (state: RootState) => state.app.analytics.existingConfiguration,
    (state: RootState, _analyticsPageType: AnalyticsPageType) => state.app.currentCatalogId,
    (_state: RootState, analyticsPageType: AnalyticsPageType) => analyticsPageType,

  ],
  (existingConfiguration, catalogId, _analyticsPageType) => find(
    existingConfiguration?.[catalogId] || [], (config) => (
      config.configurationType === ConfigurationType[_analyticsPageType]
    ),
  ),
);

export const getAverageChange = createSelector(
  [
    (state: RootState) => state.app.analytics.distributionCharts,
    (state: RootState) => state.app.analytics.currentPrePostConfigurationId,
  ],
  (distributionCharts, configurationId): AverageChangeRow[] => {
    if (!configurationId) {
      return [];
    }
    const distributionData = distributionCharts?.[configurationId];
    if (!distributionData) {
      return [];
    }

    return Object.entries(distributionData).filter(([key]) => key !== 'nps' && key !== 'csat').map(([key, disto]) => {
      const dist: Distribution = disto as Distribution;
      const isInvalid = (dist.pre?.average === null
        || dist.pre?.average === undefined)
      && (dist.post?.average === null
        || dist.post?.average === undefined
      );

      const averageChangeData: AverageChangeRow = {
        id: key,
        question: dist.questionText ? extractTextFromHTML(dist.questionText) : '',
        preAverage: dist.pre?.average || 0,
        postAverage: dist.post?.average || 0,
        changePercent: 0,
      };
      if (isInvalid) {
        // If all the values are invalid, return as is
        // The state handled on UI side
        return averageChangeData;
      }
      if (!isInvalid) {
        // to avoid division by zero
        if (!dist.pre?.average) {
          averageChangeData.changePercent = 0;
          return averageChangeData;
        }
        const postAvg = dist.post?.average || 0;
        const change = ((postAvg - dist.pre.average) / dist.pre.average) * 100;
        const changePercent = parseFloat(change.toFixed(2));
        averageChangeData.changePercent = changePercent;
      }
      return averageChangeData;
    });
  },
);

export const getQuantilesDistribution = createSelector(
  [
    (state: RootState) => state.app.analytics.distributionCharts,
    (state: RootState) => state.app.analytics.currentPrePostConfigurationId,
  ],
  (distributionCharts, configurationId): QuantileDistribution[] => {
    if (!configurationId) {
      return [];
    }
    const distributionData = distributionCharts?.[configurationId];
    if (!distributionData) {
      return [];
    }

    return Object.entries(distributionData).filter(([key]) => key !== 'nps' && key !== 'csat').map(([key, disto]) => {
      const dist: Distribution = disto as Distribution;
      const { leftLimit, rightLimit, quantileThresholds, questionText, post, pre } = dist;
      const mediumStart = quantileThresholds[0];
      const mediumEnd = quantileThresholds[1];

      // If only 2 values (e.g., scale 1-2), skip medium bucket
      const hasNoMedium = (pre?.quantiles?.[1] === 0 && post?.quantiles?.[1] === 0);
      // Gap between thresholds determines bucket calculation
      const thresholdGap = mediumEnd - mediumStart;

      const getThresholds = (): Thresholds => {
        if (hasNoMedium) {
          // Medium bucket hidden by UI; split range into low and high only
          const lowEnd = mediumStart < rightLimit ? mediumStart : leftLimit;
          const highStart = Math.min(lowEnd + 1, rightLimit);
          return {
            low: [leftLimit, lowEnd],
            medium: [mediumStart, mediumEnd], // unused but required by type
            high: [highStart, rightLimit],
          };
        }

        // Gap 0: Single threshold value, each bucket is a single point
        // e.g., thresholds [3, 3] on scale 1-5 → low: 1, medium: 3, high: 5
        if (thresholdGap === 0) {
          return {
            low: [leftLimit, leftLimit],
            medium: [mediumStart, mediumEnd],
            high: [rightLimit, rightLimit],
          };
        }

        // Gap > 1: Normal distribution with distinct ranges
        // e.g., thresholds [2, 5] on scale 0-7 → low: 0-2, medium: 3-4, high: 5-7
        return {
          low: [leftLimit, mediumStart],
          medium: [mediumStart + 1, mediumEnd],
          high: [mediumEnd + 1, rightLimit],
        };
      };

      return {
        id: key,
        question: questionText ? extractTextFromHTML(questionText) : '',
        preDistribution: {
          low: pre?.quantiles?.[0] ?? 0,
          medium: pre?.quantiles?.[1] ?? 0,
          high: pre?.quantiles?.[2] ?? 0,
        },
        postDistribution: {
          low: post?.quantiles?.[0] ?? 0,
          medium: post?.quantiles?.[1] ?? 0,
          high: post?.quantiles?.[2] ?? 0,
        },
        thresholds: getThresholds(),
      };
    });
  },
);

export const shouldReloadCharts = createSelector(
  [
    (state: RootState) => state.app.analytics.surveyCharts,
    (state: RootState) => state.app.analytics.currentPrePostConfigurationId,
  ],
  (surveyCharts, configurationId) => (configurationId ? surveyCharts?.[configurationId]?.shouldReload : false),
);

export const isNpsCsatConfigurable = createSelector(
  [
    (state: RootState) => state.app.analytics.npsCsatConfig,
  ],
  (npsCsatConfig) => {
    const npsQuestionCount = npsCsatConfig?.eligibleQuestions?.filter(q => q.metricEligibleForNps === true)?.length || 0;
    const csatQuestionCount = npsCsatConfig?.eligibleQuestions?.filter(q => q.metricEligibleForCsat === true)?.length || 0;
    return npsQuestionCount > 0 || csatQuestionCount > 0;
  },
);

export const getNpsQuestions = createSelector(
  [
    (state: RootState) => state.app.analytics.npsCsatConfig,
  ],
  (npsCsatConfig) => npsCsatConfig?.eligibleQuestions?.filter(q => q.metricEligibleForNps === true),
);

export const getCsatQuestions = createSelector(
  [
    (state: RootState) => state.app.analytics.npsCsatConfig,
  ],
  (npsCsatConfig) => npsCsatConfig?.eligibleQuestions?.filter(q => q.metricEligibleForCsat === true),
);

export const getNpsCsatConfiguration = (state: RootState) => state.app.analytics.npsCsatConfig;

export const getNpsCsatSummary = (state: RootState) => state.app.analytics.npsCsatSummary;
export const getNpsCsatSummaryLoading = (state: RootState) => state.app.analytics.npsCsatSummaryLoading;

export const getPrePostNpsCsatSummary = createSelector(
  [
    (state: RootState) => state.app.analytics.distributionCharts,
    (state: RootState) => state.app.analytics.currentPrePostConfigurationId,
  ],
  (distributionCharts, configurationId): NpsCsatSummary | null => {
    if (!configurationId) {
      return null;
    }
    const distributionData = distributionCharts?.[configurationId];
    if (!distributionData) {
      return null;
    }
    const npsSummary = distributionData.nps as NpsSummary || null;
    const csatSummary = distributionData.csat as CsatSummary || null;
    if (!npsSummary && !csatSummary) {
      return null;
    }
    return {
      nps: npsSummary,
      csat: csatSummary,
    };
  },
);
